import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { metersApi } from '../axios';
import { useLanguage } from '../hooks/useLanguage';
import { MeterRequest, MeterUpdateRequest } from '../types/asset';

export function useMetersQuery(search?: string, page?: number, nameOrder?: 'asc' | 'desc') {
  return useQuery({
    queryKey: ['meters', search, page, nameOrder],
    queryFn: () => metersApi.getMeters(search, page, nameOrder),
  });
}

// unused for now
export function useMeterQuery(id: number) {
  return useQuery({
    queryKey: ['meter', id],
    queryFn: () => metersApi.getMeter(id),
    enabled: !!id,
  });
}

// unused for now
export function useCreateMeterMutation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const lang = useLanguage();

  return useMutation({
    mutationFn: (meterData: MeterRequest) => metersApi.createMeter(meterData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meters'] });
      enqueueSnackbar(lang.assets.meterCreated, {
        variant: 'success',
      });
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      const errorMessage = error.response?.data?.errors?.join(', ') || error.message;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
}

export function useUpdateMeterMutation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const lang = useLanguage();

  return useMutation({
    mutationFn: ({ id, meterData }: { id: number; meterData: MeterUpdateRequest }) =>
      metersApi.updateMeter(id, meterData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meters'] });
      queryClient.invalidateQueries({ queryKey: ['meter', variables.id] });
      enqueueSnackbar(lang.assets.meterUpdated, { variant: 'success' });
    },
    onError: (error: AxiosError<{ errors?: string[]; error_code?: string }>) => {
      const data = error.response?.data;
      const errorMessage =
        data?.error_code === 'meter-in-use'
          ? lang.assets.meters.meterInUseError
          : data?.errors?.join(', ') || error.message;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
}

export function useDuplicateMeterMutation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const lang = useLanguage();

  return useMutation({
    mutationFn: (id: number) => metersApi.duplicateMeter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meters'] });
      enqueueSnackbar(lang.assets.meterDuplicated, {
        variant: 'success',
      });
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      const errorMessage = error.response?.data?.errors?.join(', ') || error.message;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
}
